import { RecurringFrequency } from '../generated/graphql';
import { FormRecurringMonetaryAmountValue } from '../UI';
import { InvestmentFormValues } from './types';

/**
 * A recurring contribution is modeled as coupled "pairs":
 * - the **income** pair (income source + percentage), and
 * - the **manual** pair (amount + frequency).
 *
 * Each pair is "both or neither": engaging one field requires the other to save. The employee side
 * (income + manual) is mutually exclusive — engaging one pair locks the other. A future
 * employer-match pair slots in as an *independent* pair here: it coexists with the employee side
 * (an account can have both an employee and an employer contribution) rather than being mutually
 * exclusive, so it would get its own descriptor and live outside `employeeContributionPairs`.
 *
 * These predicates are pure functions over a normalized {@link ContributionFieldValues} so the
 * shared RHF form (TT modal / investment side-over) and the advisor PFS form (which holds the
 * fields as local state) can share one source of truth.
 */
export interface ContributionFieldValues {
  incomeSourceID?: string | null;
  percentage?: number | null;
  amount?: number | null;
  frequency?: RecurringFrequency | null;
}

// A monetary/percentage field counts as "set" only when positive — 0 and blank both mean "unset".
const hasPositive = (value?: number | null) => value !== null && value !== undefined && value > 0;
const hasFrequency = (frequency?: RecurringFrequency | null) =>
  frequency !== null && frequency !== undefined && frequency !== RecurringFrequency.Unknown;
const hasIncomeSource = (incomeSourceID?: string | null) => !!incomeSourceID;

export type ContributionFieldName = 'incomeSourceID' | 'percentage' | 'amount' | 'frequency';

const fieldIsSet: Record<ContributionFieldName, (v: ContributionFieldValues) => boolean> = {
  incomeSourceID: (v) => hasIncomeSource(v.incomeSourceID),
  percentage: (v) => hasPositive(v.percentage),
  amount: (v) => hasPositive(v.amount),
  frequency: (v) => hasFrequency(v.frequency),
};

export interface ContributionPair {
  key: 'income' | 'manual';
  fields: readonly ContributionFieldName[];
  /** At least one field in the pair is set. */
  isEngaged: (v: ContributionFieldValues) => boolean;
  /** Every field in the pair is set — the pair is savable. */
  isComplete: (v: ContributionFieldValues) => boolean;
}

const makePair = (key: ContributionPair['key'], fields: readonly ContributionFieldName[]): ContributionPair => ({
  key,
  fields,
  isEngaged: (v) => fields.some((field) => fieldIsSet[field](v)),
  isComplete: (v) => fields.every((field) => fieldIsSet[field](v)),
});

export const incomePair = makePair('income', ['incomeSourceID', 'percentage']);
export const manualPair = makePair('manual', ['amount', 'frequency']);

/**
 * The employee side is mutually exclusive. Order is priority (highest first): the income pair wins
 * ties so an edited income-linked account (whose amount/frequency are derived values) is never
 * mistaken for a manual entry.
 */
export const employeeContributionPairs = [incomePair, manualPair] as const;

/** The active pair in a mutually-exclusive group is the highest-priority engaged pair, if any. */
const activePair = (group: readonly ContributionPair[], v: ContributionFieldValues) =>
  group.find((pair) => pair.isEngaged(v)) ?? null;

/** A pair's inputs are locked when a different, higher-priority pair in its group is engaged. */
export const isPairLocked = (
  pair: ContributionPair,
  group: readonly ContributionPair[],
  v: ContributionFieldValues
) => {
  const active = activePair(group, v);
  return active !== null && active.key !== pair.key;
};

/** Engaged-but-incomplete pairs in a group — these block the save ("both or neither"). */
export const incompleteContributionPairs = (group: readonly ContributionPair[], v: ContributionFieldValues) =>
  group.filter((pair) => pair.isEngaged(v) && !pair.isComplete(v));

/** True when `pair` is engaged but the given field is the (or a) missing half — for field validators. */
export const isContributionFieldMissing = (
  pair: ContributionPair,
  field: ContributionFieldName,
  v: ContributionFieldValues
) => pair.isEngaged(v) && !fieldIsSet[field](v);

/** Normalizes the shared RHF form values into {@link ContributionFieldValues}. */
export const contributionFieldValuesFromForm = (formValues: InvestmentFormValues): ContributionFieldValues => ({
  incomeSourceID: formValues.incomeSourceID,
  percentage: formValues.recurringContributionPercentage,
  amount: formValues.recurringContribution?.amount,
  frequency: formValues.recurringContribution?.frequency,
});

/**
 * The manual pair's data point group can't be nulled (it's non-nullable on the investment), so a
 * cleared manual contribution is persisted as a $0/Unknown sentinel data point rather than deleted.
 * Reading it back must undo that sentinel — otherwise a cleared contribution reopens showing "$0" /
 * an unmatched frequency instead of a blank, editable pair.
 */
export const normalizeManualContributionValue = (
  value: FormRecurringMonetaryAmountValue
): FormRecurringMonetaryAmountValue => ({
  amount: hasPositive(value.amount) ? value.amount : null,
  frequency: hasFrequency(value.frequency) ? value.frequency : null,
});
